import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as PEvento, type ReactNode } from 'react';
import { t } from '../../i18n/idioma';
import { useIdioma } from '../../i18n/useIdioma';
import { useFechar } from '../../input/useAtalhos';
import { useRedes } from './ChatAtalhos';
import type { ModoTela } from './useModoTela';
import './guia.css';

/**
 * As boas-vindas e o guia inicial (docs/techspec/techspec-boas-vindas.md; protótipo em
 * docs/prototipos/boas-vindas-prototipo.html). Substitui o passo a passo na tela (o antigo DicasHud).
 *
 * Abre no primeiro login do personagem (`tutorial`) e em Mais › Guia do jogo. Cada tela é uma imagem (um recorte da
 * tela do jogo, em public/guia, gerado por tools/gerar-guia.py) com as marcas desenhadas por cima, um título, uma
 * frase e até 3 dicas. As marcas usam as coordenadas da foto original: `R` é o recorte que a imagem mostra.
 *
 * A arte é desenhada numa largura fixa (820 no PC, 560 no celular) e escala para caber: o texto das marcas fica do
 * mesmo tamanho em relação à imagem em qualquer tela.
 */

type R = readonly [number, number, number, number];
type Item = readonly [ReactNode, ReactNode];

const DESENHO_PC = 820;
const DESENHO_CEL = 560;
const img = (nome: string) => `/guia/${nome}.webp`;
const ITENS = '/sprites/itens';

/* ───────── as peças de uma cena ───────── */

const pos = (r: R, x: number, y: number): CSSProperties => ({
  left: `${(x - r[0]) / r[2] * 100}%`,
  top: `${(y - r[1]) / r[3] * 100}%`
});
const caixa = (r: R, b: R, pad = 4): CSSProperties => ({
  left: `${(b[0] - pad - r[0]) / r[2] * 100}%`,
  top: `${(b[1] - pad - r[1]) / r[3] * 100}%`,
  width: `${(b[2] + pad * 2) / r[2] * 100}%`,
  height: `${(b[3] + pad * 2) / r[3] * 100}%`
});

function Anel({ r, b, pad = 4, apagado = false, cor }: { r: R; b: R; pad?: number; apagado?: boolean; cor?: string }) {
  return <div className={`gv-anel${apagado ? ' is-apagado' : ''}`} style={{ ...caixa(r, b, pad), ...(cor ? { borderColor: cor } : {}) }} />;
}
const Num = ({ r, x, y, n }: { r: R; x: number; y: number; n: number }) => <div className="gv-num" style={pos(r, x, y)}>{n}</div>;
function Rotulo({ style, sub, children }: { style?: CSSProperties; sub?: ReactNode; children: ReactNode }) {
  return <div className="gv-rotulo" style={style}>{sub && <b>{sub}</b>}{children}</div>;
}
/** A imagem do recorte, numa caixa com a proporção dele: as marcas vão por cima, em %. */
function Recorte({ nome, w, h, className = '', style, children }: {
  nome: string; w: number; h: number; className?: string; style?: CSSProperties; children?: ReactNode;
}) {
  return (
    <div className={`gv-recorte ${className}`} style={{ aspectRatio: `${w} / ${h}`, ...style }}>
      <img src={img(nome)} alt="" draggable={false} />
      {children}
    </div>
  );
}
const Fundo = ({ nome = 'fundo' }: { nome?: string }) => <div className="gv-fundo"><img src={img(nome)} alt="" draggable={false} /></div>;
const Seta = ({ w, h, d }: { w: number; h: number; d: string }) => (
  <svg className="gv-seta" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true">
    <defs><marker id="gv-ponta" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto"><path d="M0 0L8 4L0 8z" fill="#e6c452" /></marker></defs>
    <path d={d} stroke="#e6c452" strokeWidth="2.5" fill="none" markerEnd="url(#gv-ponta)" />
  </svg>
);
const Tecla = ({ children, larga = false, aperta = false }: { children: ReactNode; larga?: boolean; aperta?: boolean }) =>
  <span className={`gv-tecla${larga ? ' is-larga' : ''}${aperta ? ' is-aperta' : ''}`}>{children}</span>;
const Ico = ({ src }: { src: string }) => <span className="gv-ico"><img src={src} alt="" draggable={false} /></span>;
const K = ({ children }: { children: ReactNode }) => <span className="gv-ico"><span className="gv-k">{children}</span></span>;
const Balao = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v7a2.5 2.5 0 0 1-2.5 2.5H11l-4.5 3.5V16h0A2.5 2.5 0 0 1 4 13.5z" /><path d="M8.5 10h.01M12 10h.01M15.5 10h.01" /></svg>
);

/** Um sprite do jogo animado em CSS: lê o JSON da folha (célula, quadros, fps, âncora). */
interface Folha { src: string; w: number; h: number; n: number; fps: number; ax: number; ay: number; linhas: number }
const folhas = new Map<string, Promise<Folha>>();
function carregarFolha(json: string): Promise<Folha> {
  let p = folhas.get(json);
  if (!p) {
    p = fetch(json).then(r => r.json()).then((j: { imagem: string; celula: [number, number]; quadros: number; fps?: number; ancoraX: number; ancoraY: number; linhas?: number }) => ({
      src: json.slice(0, json.lastIndexOf('/') + 1) + j.imagem,
      w: j.celula[0], h: j.celula[1], n: j.quadros, fps: j.fps ?? 8, ax: j.ancoraX, ay: j.ancoraY, linhas: j.linhas ?? 5
    }));
    folhas.set(json, p);
  }
  return p;
}
function Sprite({ json, linha, escala = 1, espelhar = false, style }: {
  json: string; linha: number; escala?: number; espelhar?: boolean; style: CSSProperties;
}) {
  const [f, setF] = useState<Folha | null>(null);
  useEffect(() => {
    let vivo = true;
    void carregarFolha(json).then(x => { if (vivo) setF(x); }).catch(() => {});
    return () => { vivo = false; };
  }, [json]);
  if (!f) return null;
  return (
    <div
      className="gv-sprite"
      style={{
        ...style,
        width: f.w * escala, height: f.h * escala, marginLeft: -f.ax * escala, marginTop: -f.ay * escala,
        backgroundImage: `url(${f.src})`, backgroundSize: `${f.w * f.n * escala}px ${f.h * f.linhas * escala}px`,
        backgroundPositionY: -linha * f.h * escala,
        animation: `gv-quadros ${f.n / f.fps}s steps(${f.n}) infinite`,
        ['--gv-fim' as string]: `${-f.w * f.n * escala}px`,
        ...(espelhar ? { transform: 'scaleX(-1)', transformOrigin: `${f.ax * escala}px 0` } : {})
      }}
    />
  );
}
const ESQ_ATAQUE = '/sprites/enemies/skeleton/attack/skeleton-attack.json';
const FANTASMA = '/sprites/character/ghost/idle/ghost-idle.json';

/**
 * Todas as imagens do guia, baixadas e decodificadas enquanto o jogador lê as boas-vindas: sem isso, o primeiro
 * "Fazer o tutorial" esperava o download e a decodificação das imagens da tela 1 e travava.
 */
const IMAGENS = ['inventario', 'janela-cacar', 'pilula', 'tela', 'tela-cel', 'andar', 'janela-combate', 'lutar', 'fundo', 'fundo-cemiterio'];
let preCarregado = false;
function preCarregar(cel: boolean) {
  if (preCarregado) return;
  preCarregado = true;
  const nomes = IMAGENS.filter(n => (cel ? n !== 'tela' : n !== 'tela-cel'));
  for (const n of nomes) {
    const im = new Image();
    im.decoding = 'async';
    im.src = img(n);
    void im.decode?.().catch(() => {});
  }
  void carregarFolha(ESQ_ATAQUE).catch(() => {});
  void carregarFolha(FANTASMA).catch(() => {});
}

/* ───────── as telas ───────── */

interface Tela {
  id: string;
  rot: string;
  titulo: string;
  texto: string;
  /** Altura da arte no desenho (largura 820 no PC, 560 no celular). */
  alt: number;
  altCel?: number;
  itens?: readonly Item[];
  pontos?: readonly { n: number; b: R; texto: ReactNode }[];
  cena(cel: boolean, aceso: number | null): ReactNode;
}

const TELA_PC: R = [0, 0, 1915, 846];
const TELA_CEL: R = [0, 0, 390, 844];

function telas(cel: boolean, discord: string): Tela[] {
  const pontosPc = [
    { n: 1, b: [771, 245, 106, 47] as R, texto: <><b>{t('guia.tela.p1')}</b></> },
    { n: 2, b: [1645, 14, 242, 298] as R, texto: <><b>{t('guia.tela.p2')}</b> {t('guia.tela.p2d')}</> },
    { n: 3, b: [780, 760, 378, 78] as R, texto: <><b>{t('guia.tela.p3')}</b> · {t('guia.tela.p3d')}</> },
    { n: 4, b: [1186, 760, 56, 70] as R, texto: <><b>{t('guia.tela.p4')}</b> · {t('guia.tela.p4d')}</> },
    { n: 5, b: [1786, 612, 124, 214] as R, texto: <><b>{t('guia.tela.p5')}</b> · {t('guia.tela.p5d')}</> },
    { n: 6, b: [1076, 14, 68, 122] as R, texto: <><b>{t('guia.tela.p6')}</b> · {t('guia.tela.p6d')}</> }
  ];
  const pontosCel = [
    { n: 1, b: [150, 760, 90, 84] as R, texto: <><b>{t('guia.tela.c1')}</b> · {t('guia.tela.c1d')}</> },
    { n: 2, b: [32, 84, 108, 48] as R, texto: <><b>{t('guia.tela.p1')}</b></> },
    { n: 3, b: [28, 188, 56, 64] as R, texto: <><b>{t('guia.tela.c3')}</b> · {t('guia.tela.c3d')}</> },
    { n: 4, b: [266, 330, 120, 290] as R, texto: <><b>{t('guia.tela.p5')}</b> · {t('guia.tela.c4d')}</> },
    { n: 5, b: [0, 718, 390, 66] as R, texto: <><b>{t('guia.tela.p3')}</b> · {t('guia.tela.c5d')}</> },
    { n: 6, b: [0, 786, 390, 58] as R, texto: <><b>{t('guia.tela.c6')}</b></> }
  ];
  return [
    {
      id: 'cacada', rot: t('guia.cacada.rot'), titulo: t('guia.cacada.titulo'), texto: t('guia.cacada.texto'), alt: 430,
      itens: [
        [<Ico src={`${ITENS}/coin/silver_pile.png`} />, <><b>{t('guia.cacada.i1')}</b> {t('guia.cacada.i1d')}</>],
        [<K>▲</K>, <><b>{t('guia.cacada.i2')}</b> {t('guia.cacada.i2d')}</>],
        [<Ico src={`${ITENS}/mobilia/arte/mobilia-bau-metal-0.png`} />, <>{t('guia.cacada.i3')} <b>{t('guia.cacada.i3b')}</b></>]
      ],
      cena: () => (
        <>
          <Fundo />
          <div className="gv-laco">
            <div className="gv-laco__q">
              <div className="gv-moldura gv-recorte-topo"><img src={img('inventario')} alt="" draggable={false} /></div>
              <span><i>1</i>{t('guia.cacada.q1')}</span><small>{t('guia.cacada.q1d')}</small>
            </div>
            <div className="gv-laco__q">
              <div className="gv-moldura gv-recorte-topo gv-recorte-topo--cacar"><img src={img('janela-cacar')} alt="" draggable={false} /></div>
              <span><i>2</i>{t('guia.cacada.q2')}</span><small>{t('guia.cacada.q2d')}</small>
            </div>
            <div className="gv-laco__q">
              <div className="gv-moldura gv-bau">
                <img src={`${ITENS}/coin/silver_pile.png`} alt="" style={{ width: 64 }} draggable={false} />
                <img src={`${ITENS}/mobilia/arte/mobilia-bau-metal-0.png`} alt="" style={{ width: 84 }} draggable={false} />
              </div>
              <span><i>3</i>{t('guia.cacada.q3')}</span><small>{t('guia.cacada.q3d')}</small>
            </div>
            <svg className="gv-laco__flecha" style={{ left: 'calc(33.3% - 6px)' }} viewBox="0 0 34 20" aria-hidden="true"><path d="M2 10h26M20 3l9 7-9 7" /></svg>
            <svg className="gv-laco__flecha" style={{ left: 'calc(66.6% - 28px)' }} viewBox="0 0 34 20" aria-hidden="true"><path d="M2 10h26M20 3l9 7-9 7" /></svg>
            <div className="gv-laco__volta"><span>{t('guia.cacada.ciclo')}</span></div>
          </div>
        </>
      )
    },
    {
      id: 'tela', rot: t('guia.tela.rot'), titulo: t('guia.tela.titulo'), texto: cel ? t('guia.tela.textoCel') : t('guia.tela.texto'),
      alt: 362, altCel: 480,
      pontos: cel ? pontosCel : pontosPc,
      cena: (c, aceso) => {
        const r = c ? TELA_CEL : TELA_PC;
        const pts = c ? pontosCel : pontosPc;
        const marcas = pts.map(p => (
          <div key={p.n}>
            <Anel r={r} b={p.b} apagado={aceso !== p.n} />
            <Num r={r} x={Math.max(p.b[0] + 6, Math.min(p.b[0] + p.b[2] / 2, r[2] - 20))} y={Math.max(16, p.b[1] - 2)} n={p.n} />
          </div>
        ));
        if (!c) return <Recorte nome="tela" w={r[2]} h={r[3]} style={{ width: '100%' }}>{marcas}</Recorte>;
        return (
          <>
            <Fundo />
            <div className="gv-cel-meio"><Recorte nome="tela-cel" w={390} h={844} className="gv-moldura" style={{ height: '100%' }}>{marcas}</Recorte></div>
          </>
        );
      }
    },
    {
      id: 'andar', rot: t('guia.andar.rot'), titulo: t('guia.andar.titulo'), texto: cel ? t('guia.andar.textoCel') : t('guia.andar.texto'), alt: 430,
      itens: cel
        ? [[<K>☝</K>, <><b>{t('guia.andar.c1')}</b> {t('guia.andar.c1d')}</>], [<K>⚡</K>, <>{t('guia.andar.c2')} <b>{t('guia.andar.c2b')}</b></>]]
        : [[<K>BD</K>, <><b>{t('guia.andar.i1')}</b> {t('guia.andar.i1d')}</>], [<K>W</K>, <><b>WASD</b> {t('guia.andar.i2d')}</>], [<K>⇧</K>, <><b>Shift</b> {t('guia.andar.i3d')}</>]],
      cena: c => {
        const r: R = [430, 238, 560, 294];
        return (
          <Recorte nome="andar" w={r[2]} h={r[3]} style={{ width: '100%' }}>
            <svg className="gv-seta" viewBox={`0 0 ${r[2]} ${r[3]}`} preserveAspectRatio="none" aria-hidden="true">
              <path className="gv-tracejado" d={`M${700 - r[0]} ${452 - r[1]} Q ${790 - r[0]} ${452 - r[1]} ${850 - r[0]} ${500 - r[1]}`} />
            </svg>
            <div className="gv-alvo-chao" style={pos(r, 852, 502)} />
            {c
              ? <Rotulo style={pos(r, 880, 470)} sub={t('guia.andar.toque')}>{t('guia.andar.aqui')}</Rotulo>
              : <div className="gv-mouse" style={pos(r, 884, 470)} />}
            {!c && (
              <>
                <div className="gv-wasd"><span /><Tecla aperta>W</Tecla><span /><Tecla>A</Tecla><Tecla>S</Tecla><Tecla>D</Tecla></div>
                <div className="gv-shift"><Tecla larga>Shift</Tecla></div>
              </>
            )}
          </Recorte>
        );
      }
    },
    {
      id: 'equipe', rot: t('guia.equipe.rot'), titulo: t('guia.equipe.titulo'), texto: cel ? t('guia.equipe.textoCel') : t('guia.equipe.texto'), alt: 430,
      itens: [
        [<Ico src={`${ITENS}/mobilia/arte/mobilia-bau-comum-0.png`} />, <><b>{t('guia.equipe.i1')}</b> {t('guia.equipe.i1d')}</>],
        cel ? [<K>☝</K>, <><b>{t('guia.equipe.c2')}</b> {t('guia.equipe.c2d')}</>] : [<K>2×</K>, <><b>{t('guia.equipe.i2')}</b> {t('guia.equipe.i2d')}</>],
        [<Ico src={`${ITENS}/coin/silver_pile.png`} />, <><b>{t('guia.equipe.i3')}</b> {t('guia.equipe.i3d')}</>]
      ],
      cena: () => {
        // O inventário (496×486) no meio, com 250 de cada lado para os rótulos.
        const iw = 496, ih = 486;
        const r: R = [-250, -20, iw + 500, ih + 40];
        return (
          <>
            <Fundo />
            <div style={{ position: 'absolute', left: `${250 / r[2] * 100}%`, top: `${20 / r[3] * 100}%`, width: `${iw / r[2] * 100}%` }}>
              <Recorte nome="inventario" w={iw} h={ih} className="gv-moldura" style={{ width: '100%' }} />
            </div>
            <Anel r={r} b={[238, 76, 46, 44]} pad={2} />
            <Anel r={r} b={[287, 76, 46, 44]} pad={2} />
            <Anel r={r} b={[86, 84, 60, 134]} pad={2} />
            <Rotulo style={pos(r, 556, 60)} sub={t('guia.equipe.r1')}>{cel ? t('guia.equipe.r1dCel') : t('guia.equipe.r1d')}</Rotulo>
            <Rotulo style={{ ...pos(r, 0, 132), left: 'auto', right: `${(r[0] + r[2] - 72) / r[2] * 100}%` }} sub={t('guia.equipe.r2')}>{t('guia.equipe.r2d')}</Rotulo>
            <Seta w={r[2]} h={r[3]} d={`M${552 - r[0]} ${82 - r[1]} L${342 - r[0]} ${96 - r[1]}`} />
          </>
        );
      }
    },
    {
      id: 'combate', rot: t('guia.combate.rot'), titulo: t('guia.combate.titulo'), texto: t('guia.combate.texto'), alt: 430,
      itens: [
        [<Ico src={`${ITENS}/weapons/large-battle-axe.png`} />, <><b>{t('guia.combate.i1')}</b> {t('guia.combate.i1d')}</>],
        [<Ico src={`${ITENS}/potions/healing.png`} />, <>{t('guia.combate.i2')} <b>75%</b></>],
        [<Ico src={`${ITENS}/potions/mana.png`} />, <>{t('guia.combate.i3')} <b>75%</b></>]
      ],
      cena: () => {
        // A janela Combate (916,110 · 404×400 na foto) à direita; os rótulos à esquerda.
        const j: R = [916, 110, 404, 400];
        const H = j[3] + 28, W = H * DESENHO_PC / 430, L = j[0] - 420, T = j[1] - 14;
        const r: R = [L, T, W, H];
        return (
          <>
            <Fundo />
            <div style={{ position: 'absolute', left: `${420 / W * 100}%`, top: `${14 / H * 100}%`, width: `${j[2] / W * 100}%` }}>
              <Recorte nome="janela-combate" w={j[2]} h={j[3]} className="gv-moldura" style={{ width: '100%' }} />
            </div>
            <Anel r={r} b={[932, 181, 372, 53]} pad={3} />
            <Anel r={r} b={[928, 350, 380, 132]} pad={2} />
            <Rotulo style={pos(r, 600, 186)} sub={t('guia.combate.r1')}>{t('guia.combate.r1d')}</Rotulo>
            <Rotulo style={pos(r, 560, 392)} sub={t('guia.combate.r2')}>{t('guia.combate.r2d')}</Rotulo>
            <Seta w={W} h={H} d={`M${820 - L} ${208 - T} L${922 - L} ${207 - T}`} />
            <Seta w={W} h={H} d={`M${812 - L} ${414 - T} L${918 - L} ${414 - T}`} />
          </>
        );
      }
    },
    {
      id: 'cacar', rot: t('guia.cacar.rot'), titulo: t('guia.cacar.titulo'), texto: t('guia.cacar.texto'), alt: 430,
      itens: [
        [<K>NV</K>, <>{t('guia.cacar.i1')} <b>{t('guia.cacar.i1b')}</b></>],
        [<K>☾</K>, <>{t('guia.cacar.i2')} <b>{t('guia.cacar.i2b')}</b></>],
        [<Ico src={`${ITENS}/mobilia/arte/mobilia-bau-metal-0.png`} />, <>{t('guia.cacar.i3')} <b>{t('guia.cacar.i3b')}</b></>]
      ],
      cena: () => {
        const j: R = [72, 64, 700, 693];
        const W = DESENHO_PC * 1.62, H = 430 * 1.62;
        const r: R = [72 - 60, 64 - 4, W, H];
        return (
          <>
            <Fundo />
            <div style={{ position: 'absolute', left: `${60 / W * 100}%`, top: `${4 / H * 100}%`, width: `${j[2] / W * 100}%` }}>
              <Recorte nome="janela-cacar" w={j[2]} h={j[3]} className="gv-moldura" style={{ width: '100%' }} />
            </div>
            <Num r={r} x={82} y={118} n={1} /><Anel r={r} b={[80, 110, 184, 90]} pad={2} />
            <Num r={r} x={282} y={178} n={2} /><Anel r={r} b={[274, 172, 490, 74]} pad={2} />
            <Num r={r} x={282} y={404} n={3} /><Anel r={r} b={[274, 404, 452, 78]} pad={2} />
            <Num r={r} x={616} y={712} n={4} /><Anel r={r} b={[608, 704, 156, 46]} pad={2} />
            <div className="gv-cacar-lado">
              <Rotulo style={{ position: 'relative' }} sub={`1 · ${t('guia.cacar.r1')}`}>{t('guia.cacar.r1d')}</Rotulo>
              <Rotulo style={{ position: 'relative' }} sub={`2 · ${t('guia.cacar.r2')}`}>{t('guia.cacar.r2d')}</Rotulo>
              <Rotulo style={{ position: 'relative' }} sub={`3 · ${t('guia.cacar.r3')}`}>{t('guia.cacar.r3d')}</Rotulo>
              <Rotulo style={{ position: 'relative' }} sub={`4 · ${t('guia.cacar.r4')}`}>{t('guia.cacar.r4d')}</Rotulo>
              <Recorte nome="pilula" w={372} h={36} className="gv-moldura" style={{ width: '100%', marginTop: 6 }} />
            </div>
          </>
        );
      }
    },
    {
      id: 'lutar', rot: t('guia.lutar.rot'), titulo: t('guia.lutar.titulo'), texto: cel ? t('guia.lutar.textoCel') : t('guia.lutar.texto'), alt: 430,
      itens: cel
        ? [[<K>☝</K>, <><b>{t('guia.lutar.c1')}</b> {t('guia.lutar.c1d')}</>], [<Ico src={`${ITENS}/weapons/katana.png`} />, <><b>{t('guia.lutar.c2')}</b> {t('guia.lutar.c2d')}</>]]
        : [[<K>↖</K>, <><b>{t('guia.lutar.i1')}</b> {t('guia.lutar.i1d')}</>], [<K>␣</K>, <><b>{t('guia.lutar.i2')}</b> {t('guia.lutar.i2d')}</>], [<K>F1</K>, <><b>F1–F8</b> {t('guia.lutar.i3d')}</>]],
      cena: c => {
        const r: R = [452, 268, 520, 273];
        return (
          <Recorte nome="lutar" w={r[2]} h={r[3]} style={{ width: '100%' }}>
            <div className="gv-anel gv-anel--alvo" style={{ ...pos(r, 652, 492), width: '13%', height: '9.5%', margin: '-2.6% 0 0 -6.5%' }} />
            <Sprite json={ESQ_ATAQUE} linha={2} escala={1.3} espelhar style={pos(r, 652, 494)} />
            <div className="gv-nick" style={{ ...pos(r, 652, 352), transform: 'translateX(-50%)', fontSize: 17, color: '#c9c2b2' }}>{t('guia.lutar.esqueleto')}</div>
            <div className="gv-dano" style={pos(r, 640, 340)}>-9</div>
            {!c && <div className="gv-espaco"><Tecla larga aperta>{t('guia.lutar.espaco')}</Tecla><span>{t('guia.lutar.espacoD')}</span></div>}
            <div className="gv-selo">{t('guia.lutar.selo')}</div>
          </Recorte>
        );
      }
    },
    {
      id: 'pvp', rot: t('guia.pvp.rot'), titulo: t('guia.pvp.titulo'), texto: t('guia.pvp.texto'), alt: 430, altCel: 360,
      itens: [
        [<K>⛨</K>, <>{t('guia.pvp.i1')} <b>{t('guia.pvp.i1b')}</b>{t('guia.pvp.i1d')}</>],
        [<K>⚔</K>, <><b>{t('guia.pvp.i2')}</b> {t('guia.pvp.i2d')}</>],
        [<Ico src={`${ITENS}/coin/silver_pile.png`} />, <>{t('guia.pvp.i3')} <b>{t('guia.pvp.i3b')}</b> {t('guia.pvp.i3d')}</>]
      ],
      cena: () => (
        <>
          <Fundo nome="fundo-cemiterio" />
          <div className="gv-pvp">
            <div className="gv-bloco">
              <b>{t('guia.pvp.cores')}</b>
              <div className="gv-linha"><span className="gv-nick gv-nick--inocente">Mirena</span><small><b>{t('guia.pvp.azul')}</b><br />{t('guia.pvp.azulD')}</small></div>
              <div className="gv-linha"><span className="gv-nick gv-nick--criminoso">Corvus</span><small><b>{t('guia.pvp.cinza')}</b><br />{t('guia.pvp.cinzaD')}</small></div>
              <div className="gv-linha"><span className="gv-nick gv-nick--assassino">Kael</span><small><b>{t('guia.pvp.vermelho')}</b><br />{t('guia.pvp.vermelhoD')}</small></div>
              <div className="gv-rodape">{t('guia.pvp.guardas')}</div>
            </div>
            <div className="gv-bloco">
              <b>{t('guia.pvp.morrer')}</b>
              <div className="gv-morte">
                <div className="gv-passo"><div className="gv-fig"><Sprite json={FANTASMA} linha={0} escala={1.1} style={{ left: '50%', top: '100%' }} /></div>{t('guia.pvp.m1')} <b>{t('guia.pvp.m1b')}</b></div>
                <div className="gv-ar">›</div>
                <div className="gv-passo"><div className="gv-fig"><svg width="70" height="108" viewBox="0 0 54 84" aria-hidden="true"><defs><linearGradient id="gv-ankh" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#f3dc8a" /><stop offset="1" stopColor="#9c7a22" /></linearGradient></defs><path d="M27 4c-11 0-17 8-17 17s7 15 13 19H6v9h21v31h0V49h21v-9H31c6-4 13-10 13-19S38 4 27 4zm0 9c5 0 8 4 8 8s-4 9-8 12c-4-3-8-7-8-12s3-8 8-8z" fill="url(#gv-ankh)" stroke="#3a2a0c" strokeWidth="2" /></svg></div>{t('guia.pvp.m2')} <b>{t('guia.pvp.m2b')}</b></div>
                <div className="gv-ar">›</div>
                <div className="gv-passo"><div className="gv-fig"><img src={`${ITENS}/mobilia/arte/mobilia-bau-comum-0.png`} alt="" style={{ width: 84 }} draggable={false} /></div>{t('guia.pvp.m3')} <b>{t('guia.pvp.m3b')}</b></div>
              </div>
              <div className="gv-rodape">{t('guia.pvp.banco')}</div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 'pronto', rot: t('guia.pronto.rot'), titulo: t('guia.pronto.titulo'), texto: t('guia.pronto.texto'), alt: 430, altCel: 340,
      itens: [
        [<span className="gv-ico gv-ico--dc"><Balao /></span>, <><b>Discord</b> · {discord.replace(/^https:\/\//, '')}</>],
        [<K>★</K>, <><b>{t('guia.pronto.i2')}</b> {t('guia.pronto.i2d')}</>],
        [<K>?</K>, <><b>{t('guia.pronto.i3')}</b> {t('guia.pronto.i3d')}</>]
      ],
      cena: () => (
        <>
          <Fundo nome="fundo-cemiterio" />
          <div className="gv-pronto">
            <div className="gv-check">
              <div className="is-feito"><i>✓</i><span>{t('guia.pronto.c1')}<small>{t('guia.pronto.c1d')}</small></span></div>
              <div className="is-agora"><i /><span>{t('guia.pronto.c2')}<small>{t('guia.pronto.c2d')}</small></span></div>
              <div><i /><span>{t('guia.pronto.c3')}<small>{t('guia.pronto.c3d')}</small></span></div>
              <div><i /><span>{t('guia.pronto.c4')}<small>{t('guia.pronto.c4d')}</small></span></div>
            </div>
            <div className="gv-dc">
              <div className="gv-dc__topo"><span className="gv-dc__ico"><Balao /></span><span className="gv-dc__nome">Discord</span></div>
              <div className="gv-dc__t">{t('guia.pronto.dcTitulo')}</div>
              <small>{t('guia.pronto.dcTexto')}</small>
              <a className="gv-dc__btn" href={discord} target="_blank" rel="noopener noreferrer">
                {t('guia.pronto.dcBotao')}<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8" /></svg>
              </a>
            </div>
          </div>
        </>
      )
    }
  ];
}

/* ───────── a janela ───────── */

/** Mede a largura da arte e devolve a escala do desenho (820 ou 560) para ela. O ref é de callback: a arte só existe
 *  depois da boas-vindas, e um ref de objeto não avisaria quando ela aparece. */
function useEscala(desenho: number) {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [escala, setEscala] = useState(1);
  useLayoutEffect(() => {
    if (!el) return;
    const medir = () => setEscala(el.clientWidth > 0 ? el.clientWidth / desenho : 1);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el, desenho]);
  return { ref: setEl, escala };
}

export function GuiaBoasVindas({ nome, modo, escFecha = false, onFechar, onCacar }: {
  /** O nome do personagem, na saudação. */
  nome: string;
  modo: ModoTela;
  /**
   * Esc fecha o guia? Não no primeiro login: o tutorial só termina em Pular ou no botão do fim. Ao rever
   * (Mais › Guia do jogo), sim.
   */
  escFecha?: boolean;
  /** Pular (ou Esc, com `escFecha`): fecha (quem abriu decide se isso conclui o tutorial). */
  onFechar(): void;
  /** O botão do fim: fecha e abre a janela de caçadas. */
  onCacar(): void;
}) {
  useIdioma();
  const cel = modo === 'cel';
  const redes = useRedes();
  const discord = redes.discord;
  const [i, setI] = useState(0);
  const [aceso, setAceso] = useState<number | null>(null);
  const lista = telas(cel, discord);
  const total = lista.length;
  const ir = useCallback((n: number) => { setAceso(null); setI(Math.max(0, Math.min(total, n))); }, [total]);
  // Modal mesmo sem fechar: o Esc para aqui e não fecha as janelas de trás.
  useFechar(escFecha ? onFechar : () => {}, { modal: true });
  // Depois do primeiro quadro: a boas-vindas aparece antes e o resto baixa em segundo plano.
  useEffect(() => { const id = window.setTimeout(() => preCarregar(cel), 150); return () => window.clearTimeout(id); }, [cel]);

  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); setI(x => Math.min(total, x + 1)); setAceso(null); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); setI(x => Math.max(x > 1 ? 1 : 0, x - 1)); setAceso(null); }
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [total]);

  // Arrastar para os lados no celular.
  const x0 = useRef<number | null>(null);
  const arrastar = {
    onPointerDown: (e: PEvento) => { x0.current = e.clientX; },
    onPointerUp: (e: PEvento) => {
      if (x0.current === null || !cel || i === 0) { x0.current = null; return; }
      const d = e.clientX - x0.current;
      x0.current = null;
      if (Math.abs(d) >= 40) ir(Math.max(1, i + (d < 0 ? 1 : -1)));
    }
  };

  const desenho = cel ? DESENHO_CEL : DESENHO_PC;
  const { ref: arteRef, escala } = useEscala(desenho);

  if (i === 0) {
    return (
      <div className={`gv-veu${cel ? ' is-cel' : ''}`}>
        <div className="gv gv--ola" role="dialog" aria-modal="true" aria-label={t('guia.aria')}>
          <div className="gv-arte gv-arte--ola">
            <img className="gv-ola__fundo" src={img('ola')} alt="" draggable={false} />
            <div className="gv-ola__vinheta" />
            <img className="gv-ola__logo" src="/guia/logo.webp" alt="Ultima Idle" draggable={false} />
            {Array.from({ length: 14 }, (_, n) => (
              <i key={n} className="gv-brilho" style={{ left: `${8 + (n * 53) % 84}%`, top: `${20 + (n * 31) % 55}%`, animationDelay: `${(n * 0.37) % 4}s` }} />
            ))}
          </div>
          <div className="gv-corpo">
            <div className="gv-passo gv-passo--centro">{t('guia.ola.selo')}</div>
            <h2>{t('guia.ola.titulo', { nome })}</h2>
            <p className="gv-txt">{t('guia.ola.texto1a')} <b className="gv-ouro">Ultima Idle</b>{t('guia.ola.texto1b')}</p>
            <p className="gv-txt gv-txt--suave">{t('guia.ola.texto2')}</p>
            <ul className="gv-itens">
              <li><Ico src={`${ITENS}/coin/silver_pile.png`} /><span><b>{t('guia.ola.prata')}</b> {t('guia.ola.prataD')}</span></li>
              <li><Ico src={`${ITENS}/cloth/robe.png`} /><span><b>{t('guia.ola.kit')}</b> {t('guia.ola.kitD')}</span></li>
              <li><Ico src={`${ITENS}/potions/healing.png`} /><span><b>{t('guia.ola.pocoes')}</b> {t('guia.ola.pocoesD')}</span></li>
            </ul>
          </div>
          <div className="gv-pe gv-pe--ola">
            <button type="button" className="gv-btn gv-btn--ouro gv-btn--grande" onClick={() => ir(1)}>{t('guia.ola.fazer')}</button>
            <button type="button" className="gv-pular" onClick={onFechar}>{t('guia.ola.pular')}</button>
          </div>
        </div>
      </div>
    );
  }

  const tela = lista[i - 1];
  const alt = cel ? (tela.altCel ?? Math.round(tela.alt * DESENHO_CEL / DESENHO_PC)) : tela.alt;
  const fim = i === total;
  return (
    <div className={`gv-veu${cel ? ' is-cel' : ''}`}>
      <div className="gv" role="dialog" aria-modal="true" aria-label={tela.titulo} {...arrastar}>
        <div className="gv-alca" />
        <div className="gv-arte" ref={arteRef} style={{ height: alt * escala }}>
          <div className="gv-escala" style={{ width: desenho, height: alt, transform: `scale(${escala})` }}>
            <div className="gv-cena">{tela.cena(cel, aceso)}</div>
          </div>
        </div>
        <div className="gv-corpo">
          <div className="gv-passo">{t('guia.passo', { n: i, total, nome: tela.rot })}<i /></div>
          <h2>{tela.titulo}</h2>
          <p className="gv-txt">{tela.texto}</p>
          {tela.pontos ? (
            <ul className="gv-itens gv-itens--lista">
              {tela.pontos.map(p => (
                <li key={p.n} onMouseEnter={() => setAceso(p.n)} onMouseLeave={() => setAceso(null)}
                  onClick={() => setAceso(a => (a === p.n ? null : p.n))}>
                  <span className="gv-num gv-num--lista">{p.n}</span><span>{p.texto}</span>
                </li>
              ))}
            </ul>
          ) : tela.itens && (
            <ul className="gv-itens">
              {tela.itens.map((it, k) => <li key={k}>{it[0]}<span>{it[1]}</span></li>)}
            </ul>
          )}
        </div>
        <div className="gv-pe">
          <button type="button" className="gv-pular" onClick={onFechar}>{t('guia.pular')}</button>
          <div className="gv-pontos">
            {lista.map((x, k) => (
              <button key={x.id} type="button" aria-label={x.titulo} aria-current={k + 1 === i} onClick={() => ir(k + 1)} />
            ))}
          </div>
          <button type="button" className="gv-btn" onClick={() => ir(i - 1)}>{t('guia.voltar')}</button>
          {fim
            ? <button type="button" className="gv-btn gv-btn--ouro" onClick={onCacar}>{t('guia.pronto.botao')}</button>
            : <button type="button" className="gv-btn gv-btn--ouro" onClick={() => ir(i + 1)}>{t('guia.proximo')}</button>}
        </div>
      </div>
    </div>
  );
}
